import type { Role, StaffRole, WorkspaceKey } from '../../api';
import type { DictKey } from '../../i18n';
import { menuItemFor, SECTION_WORKSPACE, workspaceOfPath } from './workspaces';

/**
 * Розділи CRM і хто їх бачить. Те, чим роль не може користуватись, приховується (не сіре).
 * Права дзеркалять перевірки API (xq/routers/*): якщо змінюєте там — змініть тут.
 */
export type Section =
  | 'today'
  | 'requests'
  | 'inbox'
  | 'tasks'
  | 'cases'
  | 'orders'
  | 'b2b'
  | 'catalog'
  | 'pricing'
  | 'collections'
  | 'inventory'
  | 'channels'
  | 'fleet'
  | 'freight'
  | 'planner'
  | 'documents'
  | 'dictionaries'
  | 'vehicleTypes'
  | 'processes'
  | 'users'
  | 'audit'
  | 'control'
  | 'notifications'
  | 'myDocuments'
  | 'countries'
  // batch: crm-partner
  | 'handovers'
  // batch: crm-sponsor — вкладка «Партнерські компанії» в налаштуваннях (без пункту меню)
  | 'partners'
  // batch: ws-core — робочі простори (фаза 2); сторінки ritual/quality/marketing/money створюють батчі C–F
  | 'workspaceHome'
  | 'overview'
  | 'ritual'
  | 'quality'
  | 'marketing'
  | 'money'
  // batch: p4-A — «Чого чекаємо і від кого» (GET /waiting: усі ролі CRM, як справи)
  | 'waiting'
  // 063/065: сигнали парсера груп і стан самого парсера (акаунти, групи, дублі)
  | 'signals'
  | 'parser'
  // 066: звіти про баги CRM — створює і бачить свої кожен; усі — власник і менеджер
  | 'reports';

const READ: StaffRole[] = ['owner', 'manager', 'operator', 'accountant', 'driver'];
const OFFICE: StaffRole[] = ['owner', 'manager', 'operator', 'accountant'];
const ADMIN: StaffRole[] = ['owner', 'manager'];

export const ACCESS: Record<Section, StaffRole[]> = {
  today: READ,
  requests: OFFICE,
  // розмови: API /conversations — лише INBOX (owner/manager/operator), бухгалтер отримує 403
  inbox: ['owner', 'manager', 'operator'],
  tasks: READ,
  cases: READ,
  orders: OFFICE,
  b2b: ['owner', 'manager', 'accountant'],
  catalog: OFFICE,
  pricing: ['owner', 'manager', 'accountant'],
  collections: OFFICE,
  inventory: OFFICE,
  channels: ADMIN,
  // водій: лише свої машини і рейси, без лізингу і підбору місць (API /vehicles звужує, /capacity/* → 403)
  fleet: ['owner', 'manager', 'operator', 'driver'],
  freight: ['owner', 'manager', 'operator'],
  planner: READ,
  documents: OFFICE,
  dictionaries: ADMIN,
  vehicleTypes: ADMIN,
  processes: ADMIN,
  users: ADMIN,
  // журнал змін (GET /audit): власник і бухгалтер
  audit: ['owner', 'accountant'],
  control: ['owner', 'manager', 'accountant'],
  // перегляд — увесь офіс; редагування — лише з правом «редагує правила країн» (перевіряє API)
  countries: OFFICE,
  notifications: READ,
  myDocuments: READ,
  // batch: crm-partner — «Передані нам»: GET /handovers/received (усі ролі CRM)
  handovers: READ,
  // batch: crm-sponsor — GET /partner-companies: owner, manager (партнерський тенант отримує 403)
  partners: ['owner', 'manager'],
  // batch: ws-core — типові ролі; явний допуск до простору (workspace_members) відкриває його розділи теж
  workspaceHome: READ,
  overview: ['owner'],
  ritual: ['owner', 'manager', 'operator'],
  quality: ADMIN,
  marketing: ADMIN,
  money: ['owner', 'accountant'],
  // batch: p4-A
  waiting: READ,
  // GET /signals, /parser/*: owner, manager, operator
  signals: ['owner', 'manager', 'operator'],
  parser: ['owner', 'manager', 'operator'],
  reports: READ,
};

/**
 * Партнерська компанія (перевізник, окремий тенант) не бачить продажів, каталогу, цін, складу, маркетингу,
 * якості й ритуальних послуг в Україні — API відповідає 403 (deps.own_tenant_only, workspaces.PARTNER_WORKSPACES).
 */
export const PARTNER_HIDDEN: readonly Section[] = [
  'requests', 'inbox', 'orders', 'b2b', 'catalog', 'pricing', 'collections', 'inventory', 'channels',
  'ritual', 'quality', 'marketing',
];
let partnerTenant = false;
/** Ставить stores/auth з /auth/me (is_partner_company). */
export function setPartnerTenant(v: boolean): void {
  partnerTenant = v;
}

/**
 * Чи бачить людина розділ: за роллю, або (для розділів нових просторів) за явним допуском до простору.
 * `grants` — ключі просторів людини (me.workspaces).
 */
export function canSee(role: Role | null | undefined, section: Section, grants?: readonly string[]): boolean {
  if (!role || role === 'client') return false;
  if (partnerTenant && PARTNER_HIDDEN.includes(section)) return false;
  if (ACCESS[section].includes(role)) return true;
  const ws = SECTION_WORKSPACE[section];
  return !!ws && !!grants?.includes(ws);
}

export interface NavItem {
  section: Section;
  to: string;
  key: DictKey;
  /** Інші шляхи, на яких пункт активний. */
  match?: string[];
  /** Показувати пункт лише тим, хто НЕ бачить цей розділ (щоб не дублювати вкладку). */
  onlyWithout?: Section;
}
export interface NavGroup {
  key: DictKey;
  items: NavItem[];
}

/** Меню за роботою людей. Розділи наступних етапів додаються сюди, коли зʼявляються. */
export const NAV: NavGroup[] = [
  {
    key: 'nav.group.work',
    items: [
      { section: 'today', to: '/', key: 'nav.today', match: ['/control', '/today'] },
      { section: 'requests', to: '/leads', key: 'nav.requests', match: ['/inbox'] },
      { section: 'signals', to: '/signals', key: 'nav.signals', match: ['/parser'] },
      { section: 'tasks', to: '/tasks', key: 'nav.tasks' },
      { section: 'cases', to: '/cases', key: 'nav.cases' },
      // batch: p4-A
      { section: 'waiting', to: '/waiting', key: 'nav.waiting' },
      // batch: crm-partner
      { section: 'handovers', to: '/handovers', key: 'hr.nav' },
    ],
  },
  {
    key: 'nav.group.trade',
    items: [
      { section: 'orders', to: '/orders', key: 'nav.orders', match: ['/b2b'] },
      { section: 'catalog', to: '/products', key: 'nav.catalog', match: ['/pricing'] },
      { section: 'collections', to: '/collections', key: 'nav.collections' },
      { section: 'inventory', to: '/inventory', key: 'nav.inventory', match: ['/channels'] },
    ],
  },
  {
    key: 'nav.group.logistics',
    items: [
      { section: 'fleet', to: '/fleet', key: 'nav.fleet' },
      { section: 'freight', to: '/freight', key: 'nav.freight' },
    ],
  },
  {
    key: 'nav.group.team',
    items: [
      { section: 'planner', to: '/planner', key: 'nav.planner' },
      { section: 'documents', to: '/documents', key: 'nav.documents', match: ['/my-documents'] },
      // окремим пунктом — лише для тих, хто не бачить архів; іншим це вкладка в «Документах»
      { section: 'myDocuments', to: '/my-documents', key: 'nav.myDocuments', onlyWithout: 'documents' },
    ],
  },
  {
    key: 'nav.group.settings',
    items: [
      { section: 'dictionaries', to: '/dictionaries', key: 'nav.dictionaries' },
      { section: 'vehicleTypes', to: '/vehicle-types', key: 'nav.vehicleTypes' },
      { section: 'processes', to: '/processes', key: 'nav.processes' },
      { section: 'countries', to: '/countries', key: 'nav.countries' },
      { section: 'users', to: '/settings', key: 'nav.users' },
      { section: 'reports', to: '/reports', key: 'nav.reports' },
    ],
  },
];

export function navFor(role: Role | null | undefined, groups: NavGroup[] = NAV): NavGroup[] {
  return groups
    .map((g) => ({ ...g, items: g.items.filter((it) => canSee(role, it.section) && !(it.onlyWithout && canSee(role, it.onlyWithout))) }))
    .filter((g) => g.items.length > 0);
}

function matches(path: string, prefix: string): boolean {
  if (prefix === '/') return path === '/';
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** Пункт меню, що відповідає шляху (для активного стану і хлібних крихт). */
export function findNav(path: string, groups: NavGroup[] = NAV): { group: NavGroup; item: NavItem } | null {
  for (const group of groups) {
    for (const item of group.items) {
      if ([item.to, ...(item.match ?? [])].some((p) => matches(path, p))) return { group, item };
    }
  }
  return null;
}

/** Вкладки всередині пункту меню: розділи, що злилися в один пункт. */
export const SUBNAV: Record<string, { to: string; key: DictKey; section: Section }[]> = {
  requests: [
    { to: '/leads', key: 'nav.leads', section: 'requests' },
    { to: '/inbox', key: 'nav.inbox', section: 'inbox' },
  ],
  orders: [
    { to: '/orders', key: 'nav.orders', section: 'orders' },
    { to: '/b2b', key: 'nav.b2b', section: 'b2b' },
  ],
  catalog: [
    { to: '/products', key: 'nav.products', section: 'catalog' },
    { to: '/pricing', key: 'nav.pricing', section: 'pricing' },
  ],
  documents: [
    { to: '/documents', key: 'nav.documents', section: 'documents' },
    { to: '/my-documents', key: 'nav.myDocuments', section: 'myDocuments' },
  ],
  inventory: [
    { to: '/inventory', key: 'nav.stock', section: 'inventory' },
    { to: '/channels', key: 'nav.channels', section: 'channels' },
  ],
};

/** Хлібні крихти: простір (якщо відомий) / група / пункт меню / вкладка. Назва простору замінює назву групи. */
export function crumbs(path: string, workspace?: WorkspaceKey | null, search = ''): DictKey[] {
  const ws = workspace ?? workspaceOfPath(path);
  // пункт меню може бути вкладкою в адресі (напр. «Рахунки» = /money?tab=invoices) — як і бокове меню, дивимось і на ?query
  const own = ws ? menuItemFor(ws, path + search) : null;
  const hit = findNav(path);
  if (!own && !hit) return ws ? [`ws.title.${ws}` as DictKey] : [];
  const out: DictKey[] = ws ? [`ws.title.${ws}` as DictKey, (own ?? hit!.item).key] : [hit!.group.key, hit!.item.key];
  // пункт меню простору точніший за загальний (напр. «Журнал змін» у «Грошах») — він і є останньою крихтою;
  // вкладка налаштувань зберігає свою звичну назву («Партнерські компанії» → settings.partners)
  if (!hit || (ws && own && own.to !== hit.item.to)) {
    const tab = /^\/settings\/(sites|audit|security|partners|languages)\b/.exec(path);
    if (tab && out.length > 1) out[out.length - 1] = `settings.${tab[1]}` as DictKey;
    return out;
  }
  const sub = (SUBNAV[hit.item.section] ?? []).find((x) => x.to !== hit.item.to && matches(path, x.to));
  if (sub) out.push(sub.key);
  else if (matches(path, '/control')) out.push('nav.control');
  else {
    const m = /^\/settings\/(\w+)/.exec(path);
    // лише відомі вкладки налаштувань — невідомий шлях не має показувати технічний ключ
    if (m && m[1] === 'workspaces') out.push('ws.menu.workspaces');
    else if (m && m[1] !== 'users' && ['sites', 'audit', 'security', 'partners', 'languages'].includes(m[1]!)) out.push(`settings.${m[1]}` as DictKey);
  }
  return out;
}
